import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CustomerInfo, PaymentInfo } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { supabase } from '@/integrations/supabase/client';
import PixPayment from '@/components/payment/PixPayment';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import OrderSummary from '@/components/checkout/OrderSummary';
import CustomerForm from '@/components/checkout/CustomerForm';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { fetchDeliveryFeeConfiguration, DeliveryFeeConfig } from '@/lib/deliverySettings';

const formSchema = z.object({
  name: z.string().min(2, { message: 'Nome é obrigatório' }),
  phone: z.string().min(10, { message: 'Telefone é obrigatório' }),
  cpf: z.string().optional().or(z.literal('')),
  email: z.string().email({ message: 'Email inválido' }).optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  note: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof formSchema>;

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, setCustomerInfo, totalPrice: cartSubtotal } = useCart();
  const [paymentMethod] = useState<PaymentInfo['method']>('pix');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [pixKeyInfo, setPixKeyInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSearchingCpf, setIsSearchingCpf] = useState(false);

  const [deliveryFeeAmount, setDeliveryFeeAmount] = useState<number>(0);
  const [isDeliveryConfigLoading, setIsDeliveryConfigLoading] = useState(true);
  const [effectiveTotalPrice, setEffectiveTotalPrice] = useState<number>(cartSubtotal);

  useEffect(() => {
    if (cartItems && cartItems.length === 0) {
      navigate('/catalog');
    }
  }, [cartItems, navigate]);

  const { 
    data: deliveryConfig, 
    isLoading: isLoadingDeliveryFeeQuery,
    error: deliveryFeeError 
  } = useQuery<DeliveryFeeConfig | null, Error>({
    queryKey: ['delivery-fee-config-checkout'],
    queryFn: fetchDeliveryFeeConfiguration,
  });

  useEffect(() => {
    console.log("Checkout.tsx - useEffect para taxa de entrega disparado.");
    console.log("Checkout.tsx - isLoadingDeliveryFeeQuery:", isLoadingDeliveryFeeQuery);
    console.log("Checkout.tsx - deliveryFeeError:", deliveryFeeError);
    console.log("Checkout.tsx - deliveryConfig ANTES de processar:", deliveryConfig);

    setIsDeliveryConfigLoading(isLoadingDeliveryFeeQuery);
    if (!isLoadingDeliveryFeeQuery) {
      if (deliveryFeeError) {
        console.error("Checkout.tsx - Erro ao buscar configuração de taxa de entrega:", deliveryFeeError.message);
        toast.error("Não foi possível carregar as opções de entrega. Tente novamente.");
        setDeliveryFeeAmount(0);
      } else if (deliveryConfig) {
        console.log("Checkout.tsx - deliveryConfig recebido DENTRO do IF:", deliveryConfig);
        if (deliveryConfig.fee_type === 'fixed' && deliveryConfig.fixed_rate !== null && deliveryConfig.fixed_rate !== undefined) {
          console.log("Checkout.tsx - Aplicando taxa fixa. Valor de fixed_rate:", deliveryConfig.fixed_rate, "Tipo:", typeof deliveryConfig.fixed_rate);
          setDeliveryFeeAmount(Number(deliveryConfig.fixed_rate));
        } else if (deliveryConfig.fee_type === 'per_km') {
          console.warn("Checkout.tsx - Taxa por KM configurada, mas o cálculo online ainda não está implementado. Usando R$0.00 para entrega.");
          setDeliveryFeeAmount(0); 
        } else {
          console.warn("Checkout.tsx - Tipo de taxa desconhecido ou fixed_rate ausente/inválido. deliveryConfig.fee_type:", deliveryConfig.fee_type, "deliveryConfig.fixed_rate:", deliveryConfig.fixed_rate);
          setDeliveryFeeAmount(0); 
        }
      } else {
        console.warn("Checkout.tsx - Nenhuma configuração de taxa de entrega encontrada (deliveryConfig é null/undefined após carregamento e sem erro).");
        setDeliveryFeeAmount(0); 
      }
    } else {
      console.log("Checkout.tsx - Ainda carregando configuração de entrega (isLoadingDeliveryFeeQuery é true).");
    }
  }, [deliveryConfig, isLoadingDeliveryFeeQuery, deliveryFeeError]);

  useEffect(() => {
    setEffectiveTotalPrice(cartSubtotal + deliveryFeeAmount);
  }, [cartSubtotal, deliveryFeeAmount]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', phone: '', cpf: '', email: '', address: '', note: '' }
  });

  const handleReceiptUpload = (base64Image: string) => setReceiptUrl(base64Image);
  const handlePixKeyChange = (pixKey: string) => setPixKeyInfo(pixKey);

  const handleCpfSearch = async (cpf: string) => {
    if (!cpf) return;
    setIsSearchingCpf(true);
    const normalizedCpf = cpf.replace(/\D/g, '');
    try {
      const { data, error } = await supabase.functions.invoke('search-customer', { body: { cpf: normalizedCpf } });
      if (error) throw error;
      if (data && data.success && data.customer) {
        const customer = data.customer;
        toast.success('Cliente encontrado! Dados carregados.');
        setValue('name', customer.name || '');
        setValue('phone', customer.phone || '');
        setValue('cpf', customer.cpf || normalizedCpf);
        setValue('email', customer.email || '');
        setValue('address', customer.address || '');
      } else {
        console.error('Unexpected success response format:', data);
        toast.error('Resposta inesperada ao buscar cliente.');
      }
    } catch (error) {
      console.error('Error during CPF search invocation:', error);
      if (error instanceof FunctionsHttpError && error.context?.status === 404) {
        toast.warning('Cliente não encontrado. Preencha os dados manualmente.');
        setValue('name', ''); setValue('phone', ''); setValue('email', ''); setValue('address', '');
        setValue('cpf', normalizedCpf);
      } else if (error instanceof Error) {
        toast.error(`Erro ao buscar cliente: ${error.message}`);
      } else {
        toast.error('Erro desconhecido ao buscar cliente.');
      }
    } finally {
      setIsSearchingCpf(false);
    }
  };

  const onSubmit = async (formData: FormData) => {
    setLoading(true);
    if (paymentMethod === 'pix' && !receiptUrl) {
      toast.error('Por favor, envie o comprovante de pagamento PIX');
      setLoading(false);
      return;
    }
    if (paymentMethod === 'pix' && !pixKeyInfo) {
      toast.error('Por favor, informe sua chave PIX utilizada para pagamento');
      setLoading(false);
      return;
    }
    try {
      const customerInfoPayload: CustomerInfo = {
        name: formData.name,
        phone: formData.phone,
        cpf: formData.cpf || undefined,
        note: formData.note,
        email: formData.email,
        address: formData.address, 
        pixKey: pixKeyInfo || undefined,
        paymentInfo: { method: paymentMethod, receiptUrl: receiptUrl || undefined },
      };
      setCustomerInfo(customerInfoPayload); 
      navigate('/success');
    } catch (error) {
      console.error('Erro ao preparar dados para checkout:', error);
      toast.error('Erro ao preparar pedido para envio.');
    } finally {
      setLoading(false);
    }
  };

  if (!cartItems) { 
    return <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-navy-600" /></div>;
  }
  
  if (isDeliveryConfigLoading && cartItems.length > 0) {
     return (
      <main className="py-8 container mx-auto px-4 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-navy-600 inline-block" />
        <p>Carregando opções de entrega...</p>
      </main>
    );
  }

  return (
    <main className="py-8 container mx-auto px-4">
      <h1 className="text-2xl font-bold mb-6 text-center md:text-left">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <CustomerForm register={register} errors={errors} onCpfSearch={handleCpfSearch} isSearchingCpf={isSearchingCpf} />
            <div className="p-4 border rounded-md">
              <h2 className="text-lg font-semibold mb-4">2. Método de Pagamento</h2>
              <PixPayment totalAmount={effectiveTotalPrice} onReceiptUpload={handleReceiptUpload} onPixKeyChange={handlePixKeyChange} />
            </div>
            <Button type="submit" disabled={loading || !cartItems || cartItems.length === 0} className={`w-full py-3 text-lg font-semibold ${loading || !cartItems || cartItems.length === 0 ? 'opacity-70 cursor-not-allowed' : ''}`}>
              {loading ? (<span className="flex items-center justify-center"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processando...</span>) : ('Finalizar Pedido e Enviar')}
            </Button>
          </form>
        </div>
        <div className="lg:col-span-1">
          <OrderSummary 
            cartItems={cartItems} 
            subtotal={cartSubtotal} 
            deliveryFee={deliveryFeeAmount} 
            totalPrice={effectiveTotalPrice} 
            isLoadingDeliveryFee={isDeliveryConfigLoading}
          />
        </div>
      </div>
    </main>
  );
};

export default Checkout;
